import { useContext, useEffect } from 'react';
import clsx from 'clsx';
import { BrowserRouter, Switch, Route } from 'react-router-dom';
import { NonAuthRoutes, AuthRoutes, themes } from './enums';
import AuthRoute from '../components/AuthRoute';
import Login from '../pages/Login';
import Search from '../pages/Search';
import AdminSearch from '../pages/AdminSearch';
import ProfileMenu from '../components/ProfileMenu';
import ToolbarLocation from '../components/ToolbarLocation';
import { LogoHorizontalDark, ManheimRepairifyLogo, CarvanaRepairifyLogo } from '../components/Logo';
import MainListItems from '../components/MainListItems';
import Copyright from '../components/Copyright';
import { Drawer, AppBar, Toolbar}  from '@material-ui/core';
import CartDrawer from '../components/Cart/CartDrawer';
import Cart from '../components/Cart/Cart';
import OrderDetails from '../pages/OrderDetails';
import Orders from '../pages/Orders';
import { CartContext } from '../context/CartContext'; 
import SearchHistory from '../pages/SearchHistory';
import Locations from '../pages/Locations';
import { Rules } from '../pages/Rules';
import Users from '../pages/Users';
import ResetPassword from '../pages/ResetPassword';
import { UserContext } from '../context/UserContext';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { drawerWidth, cartDrawerWidth } from './common';

const useStyles = makeStyles((theme: Theme) =>
  createStyles ({
    root: {
      display: 'flex',
      height: '100vh',
    },
    appBar: {
      zIndex: theme.zIndex.drawer + 1,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      backgroundColor: theme.palette.secondary.main,
    },
    grow: {
      flexGrow: 1,
    },
    sectionDesktop: {
      display: 'none',
      [theme.breakpoints.up('md')]: {
        display: 'flex',
      },
    },
    drawer: {
      whiteSpace: 'nowrap',
      backgroundColor: theme.palette.background.default,
    },
    drawerContainer: {
      overflow: 'auto',
      width: drawerWidth,
    },
    toolbarTitle: {
      flexGrow: 1,
      padding: theme.spacing(0, 1),
      backgroundColor: theme.palette.secondary.dark,
      borderBottom: "4px solid", 
      borderBottomColor: theme.palette.secondary.dark,
    },
    manheimToolbarTitle: {
      flexGrow: 1,
      padding: theme.spacing(0,1),
      backgroundColor: '#FFFFFF',
      borderBottomColor: theme.palette.secondary.main,
      borderBottom: "4px solid", 
    },
    carvanaToolbarTitle: {
      flexGrow: 1,
      padding: theme.spacing(1,1,0),
      backgroundColor: '#FFFFFF',
      borderBottomColor: theme.palette.primary.main,
      borderBottom: "4px solid", 
    },
    content: {
      flexGrow: 1,
      padding: theme.spacing(3),
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      background: theme.palette.background.paper,
    },
    contentLeftShift: {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginRight: cartDrawerWidth,
      marginLeft: drawerWidth
    }, 
    contentRightShift: {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: drawerWidth,
    }, 
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: theme.spacing(0, 0, 0),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar,
    },

    footer: {
      top: 'auto',
      bottom: 0,
      backgroundColor: theme.palette.background.default,
      zIndex: theme.zIndex.drawer + 1,
    },
  }),
  {index:1}
);

interface IRoutes {
  userTheme: string,
  setUserTheme: (theme: string) => void
}

export const Routes = ({userTheme, setUserTheme}: IRoutes) => {
  const cart = useContext(CartContext);
  const { state } = useContext(UserContext)
  const classes = useStyles();
  let toolbarClass = classes.toolbarTitle;
  let logo = <LogoHorizontalDark />
  
  if(state.userTheme === themes.MANHEIM){
    toolbarClass = classes.manheimToolbarTitle
    logo = <ManheimRepairifyLogo />
  } else if(state.userTheme === themes.CARVANA){
    toolbarClass = classes.carvanaToolbarTitle
    logo = <CarvanaRepairifyLogo />
  }

  useEffect(() => {
    setUserTheme(state.userTheme);
  },[state])

  return (
    <BrowserRouter>
      <div className={classes.root}>
        <AppBar 
          position="fixed" 
          className={classes.appBar}
        >
          <Toolbar className={toolbarClass}>
            {logo}
            <div className={classes.grow} />      
            <div className={classes.sectionDesktop}>    
              <ToolbarLocation/>
              <ProfileMenu/>
              <Cart/>
            </div>
          </Toolbar>
        </AppBar>
        <Drawer
          variant="permanent" 
          className={classes.drawer}
          classes={{
            paper:classes.drawer
          }}
        >
          <Toolbar />
          <div className={classes.drawerContainer}>
            <MainListItems/>
          </div>
        </Drawer>
        { state.self.locations.length > 0 && <CartDrawer /> }
        <main className={clsx(classes.content, {
          [classes.contentLeftShift]: (cart.state.isCartDrawerOpen)
          })}
        >	
          <div className={classes.toolbar} />
          <Switch>
            <Route exact path={NonAuthRoutes.unathorized} component={Login} />
            <Route exact path={NonAuthRoutes.login} component={Login} />
            <Route exact path='/reset/:token' component={ResetPassword} />
            <AuthRoute
              path={AuthRoutes.search}
              Component={Search}
            />
            <AuthRoute
              path={AuthRoutes.adminSearch}
              Component={AdminSearch}
            />
            <AuthRoute
              path={AuthRoutes.searchHistory}
              Component={SearchHistory}
            />
            <AuthRoute
              path={AuthRoutes.rules}
              Component={Rules}
            />
            <AuthRoute
              path={AuthRoutes.users}
              Component={Users}
            />
            <AuthRoute
              path={AuthRoutes.orders}
              Component={Orders}
            />
            <AuthRoute
              path={AuthRoutes.orderDetails}
              Component={OrderDetails}
            />
            <AuthRoute
              path={AuthRoutes.locations}
              Component={Locations}
            />
          </Switch>
          <AppBar className={classes.footer}>
            <Copyright/>
          </AppBar>
        </main>   
      </div>
    </BrowserRouter>
  );
};
